import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Mail } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/motion/Reveal";
import { LocationMap } from "@/components/landing/LocationMap";

const LOCATIONS = {
  napoli: {
    city: "Napoli",
    tag: "Nucleo Storico — Sede Principale",
    addresses: ["Viale Augusto 162, Napoli (NA)", "Piazzetta Aniello Falcone 1, Napoli (NA)"],
  },
  milano: {
    city: "Milano",
    tag: "Capitale economica — Sede di Milano",
    addresses: ["Corso di Porta Romana 69, Milano (MI)"],
  },
};

const Locations = () => {
  const [active, setActive] = useState("napoli");
  const loc = LOCATIONS[active];

  return (
    <section id="sedi" className="bg-pearl py-24 sm:py-32" data-testid="locations-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          index="03"
          eyebrow="Le Nostre Sedi"
          title="Presenza sul Territorio Nazionale"
          subtitle="Garantiamo assistenza legale diretta ed immediata nelle due principali piazze economiche e giuridiche del Paese: Napoli e Milano."
        />

        <Reveal delay={0.2}>
          <div className="mt-12 inline-flex flex-wrap border border-navy/15 bg-white p-1" data-testid="locations-tabs">
            {Object.entries(LOCATIONS).map(([key, l]) => (
              <button
                key={key}
                onClick={() => setActive(key)}
                data-testid={`location-tab-${key}`}
                className={`px-6 sm:px-8 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${
                  active === key ? "bg-navy text-gold" : "text-slate-500 hover:text-navy"
                }`}
              >
                {l.city}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8 grid gap-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="h-full border border-navy/10 bg-white p-8 sm:p-12 shadow-sm"
                data-testid="location-card"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold-dark">{loc.tag}</p>
                <h3 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">{loc.city}</h3>
                <div className="mt-8 flex flex-col gap-5">
                  {loc.addresses.map((a) => (
                    <div key={a} className="flex items-start gap-4">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center border border-gold/40 bg-gold/10 text-gold-dark">
                        <MapPin className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="font-medium text-navy">{a}</p>
                        <p className="text-sm text-slate-500">Riceviamo esclusivamente su appuntamento</p>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-start gap-4">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center border border-gold/40 bg-gold/10 text-gold-dark">
                      <Mail className="h-4 w-4" />
                    </span>
                    <a href="mailto:avv.giuseppenapolitano@gmail.com" className="font-medium text-navy hover:text-gold-dark transition-colors break-all">
                      avv.giuseppenapolitano@gmail.com
                    </a>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <Reveal delay={0.25} className="lg:col-span-2">
            <LocationMap cityKey={active} addresses={loc.addresses} />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Locations;
